import httpClient from './httpClient'

const END_POINT = '/customerresources'

/**
 * Why a go-live / go-offline happened. Server side it only changes the audit note on the campaign
 * event, so support can tell the softphone pausing a campaign around a long call from an agent
 * walking away. Values are the server enum names (`SoftphoneCampaignStateChangeReason`).
 */
export const SOFTPHONE_STATE_CHANGE_REASON = Object.freeze({
  AGENT_ACTION: 'AgentAction',
  WRAP_UP: 'WrapUp'
})

function reasonConfig(reason) {
  return reason ? { params: { reason } } : undefined
}

/**
 * Every softphone endpoint is scoped by account, and an account id that never loaded used to be
 * interpolated straight into the path - `/customerresources/undefined/softphone/...`, answered with
 * a 403 that says nothing about the real problem. Fail here instead, with something the agent can
 * act on.
 */
function requireAccountId(accountId) {
  if (!accountId) {
    throw new Error('Your account details are not loaded yet, so the softphone cannot start. Close the softphone window and go live again.')
  }

  return accountId
}

const SoftphoneAPI = {
  /**
   * Get a short lived Telnyx WebRTC `login_token` for the signed-in agent's softphone credential in
   * this account (ringba-x #2724).
   *
   * @param {string} accountId - The account ID
   * @returns {Promise<{ data: { token: string, expiresAt: string } }>}
   */
  async getWebRtcToken(accountId) {
    return await httpClient.get(`${END_POINT}/${requireAccountId(accountId)}/softphone/webrtc-token`)
  },

  /**
   * Go live on ONE softphone campaign. The server pauses every other active softphone campaign that
   * rings the signed-in agent in this account, then resumes this one - the one-live-campaign rule is
   * enforced there, not here.
   *
   * @param {string} accountId - The account ID
   * @param {string} campaignId - The campaign to go live on
   * @param {{ reason?: string }} [options] - Optional SOFTPHONE_STATE_CHANGE_REASON; omitted = agent action
   */
  async goLive(accountId, campaignId, { reason } = {}) {
    return await httpClient.put(`${END_POINT}/${requireAccountId(accountId)}/softphone/campaigns/${campaignId}/go-live`, null, reasonConfig(reason))
  },

  /**
   * Take the signed-in agent offline in this account: the server pauses every active softphone
   * campaign that rings them. No campaign id on purpose - whatever is live stops ringing, including a
   * campaign this window never knew about.
   *
   * @param {string} accountId - The account ID
   * @param {{ reason?: string }} [options] - Optional SOFTPHONE_STATE_CHANGE_REASON; omitted = agent action
   */
  async goOffline(accountId, { reason } = {}) {
    return await httpClient.put(`${END_POINT}/${requireAccountId(accountId)}/softphone/campaigns/go-offline`, null, reasonConfig(reason))
  }
}

export default SoftphoneAPI
